import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import Logo from '../components/Logo';
import { trackEvent } from '../components/analytics';

const AboutPage: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="About AviaGov"
        description="AviaGov is an aviation compliance platform built for regulated environments, supporting safety, training, procedure design, and aeronautical information with governance and human responsibility at the core."
      />

      <section className="py-24 bg-slate-950 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="max-w-4xl">
            <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
              Built for <br />
              <span className="text-accent">Regulated Aviation Environments</span>
            </h1>
            <p className="text-xl text-slate-400 mb-10 leading-relaxed">
              AviaGov exists to address the fragmentation between safety management, training, procedures, and aeronautical information that creates gaps in traceability, oversight, and audit confidence. The problem is not regulation; it is the lack of alignment between evidence, responsibilities, and validated decisions across the compliance chain.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">The Problem AviaGov Addresses</h2>
          <p className="text-slate-400 text-base leading-relaxed mb-6">
            Aviation compliance is not a lack of regulation. It is a lack of connection between safety findings, training actions, procedures, and evidence. When these are managed in separate systems, organizations rely on manual reconciliation, creating delay, ambiguity, and audit pressure.
          </p>
          <p className="text-slate-400 text-base leading-relaxed">
            AviaGov was built to reduce those gaps by keeping traceability, validation, and governance intact across the full compliance loop.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Built on Complementary Aviation Expertise</h2>
          <p className="text-slate-400 text-base leading-relaxed mb-6">
            AviaGov was created by joining expertise from regulatory, operational, and technical aviation domains.
            {/* Davide Raro, founder of Davide Raro Consulting, and Mike Lockwood, founder and Managing Director of Air Traffic Solutions, brought complementary experience across CAAs, ANSPs, airports, and airlines. */}
          </p>
          <p className="text-slate-400 text-base leading-relaxed mb-6">
            Our combined background spans ATM, ATC, AIM, SMS, audits, procurement, and system modernisation, balancing regulatory expectations with operational realities.
          </p>
          <p className="text-slate-400 text-base leading-relaxed">
            AviaGov reflects this balance by design: operational reality and regulatory expectation, implementation and oversight, efficiency and accountability.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-10 text-white">AviaGov Principles</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Human responsibility first</h3>
              <p className="text-slate-400 text-sm">All safety and regulatory decisions remain under accountable human authority.</p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Clear separation of roles</h3>
              <p className="text-slate-400 text-sm">Implementation and oversight are supported without blurring responsibilities.</p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Governance by design</h3>
              <p className="text-slate-400 text-sm">Validation, auditability, and traceability are built into every workflow.</p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Compliance before automation</h3>
              <p className="text-slate-400 text-sm">Assistance supports compliance; it does not replace regulatory control.</p>
            </GlowCard>
            <GlowCard>
              <h3 className="text-xl font-bold text-white mb-3">Modularity with boundaries</h3>
              <p className="text-slate-400 text-sm">Organizations activate only what is appropriate to their mandate.</p>
            </GlowCard>
          </div>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Who AviaGov Is Built For</h2>
          <ul className="text-slate-400 text-sm space-y-2 mb-6">
            <li>Civil Aviation Authorities</li>
            <li>ANSPs</li>
            <li>Airport operators</li>
            <li>Airlines and operators</li>
            <li>Regulated aviation service providers</li>
          </ul>
          <p className="text-slate-400 text-base leading-relaxed">
            Each organization uses AviaGov differently, according to its responsibilities and regulatory role.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Governance, Security & Deployment Philosophy</h2>
          <p className="text-slate-400 text-base leading-relaxed mb-6">
            AviaGov supports Public Cloud, Private Cloud, and On-Premise deployments to align with national data policies and organizational governance.
          </p>
          <ul className="text-slate-400 text-sm space-y-2 mb-6">
            <li>Organization-level data isolation</li>
            <li>Role-based access control</li>
            <li>Approval workflows and validation gates</li>
            <li>No autonomous safety or regulatory decisions</li>
          </ul>
          <p className="text-slate-400 text-base leading-relaxed">
            This approach supports legal, operational, and security requirements without compromising accountability.
          </p>
        </div>
      </section>

      <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">What AviaGov Is Not</h2>
          <p className="text-slate-400 text-base leading-relaxed">
            AviaGov is not a generic workflow tool, not an automation engine replacing accountability, and not a one-size-fits-all system. It is a regulated compliance environment designed to reflect institutional responsibilities.
          </p>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto text-center">
          <div className="mb-10 flex justify-center opacity-80">
            <Logo size="lg" />
          </div>
          <p className="text-slate-400 text-base leading-relaxed mb-10">
            AviaGov works best when applied thoughtfully to real regulatory structures, with governance, oversight, and validation preserved in every workflow.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="px-10 py-4 border border-white/10 text-white font-bold rounded-xl hover:bg-white/5" onClick={() => trackEvent('generate_lead', { form_id: 'about_discuss_org' })}>
              Discuss Your Organization
            </Link>
            <Link to="/contact" className="px-10 py-4 bg-accent text-primary font-bold rounded-xl" onClick={() => trackEvent('demo_request', { form_id: 'about_request_demo' })}>
              Request a Demo
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
